import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Button,
  Typography,
  Container,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import PropTypes from "prop-types";
import { db } from "../firebase";
import CloseButton from "../components/CloseButton";

const steps = ["Pennant Info", "Scoring Type", "Sections"];

// Helper to convert "HH:MM" to total minutes from midnight
function timeStringToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
}

export default function AddPennant({ isOpen, toggle, edit, pennantId }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);

  // We store all settings (including totalCourts) in state
  const [settings, setSettings] = useState({
    seasons: [],
    playTypes: [],
    ageGroups: [],
    sections: [],
    pennantDays: [],
    scoringTypes: [],
    participantTypes: [],
    totalCourts: 0, // Make sure to include a default
  });

  // Store all Pennants so we can check overlaps
  const [allPennants, setAllPennants] = useState([]);

  // For display of how many courts are available
  const [availableCourts, setAvailableCourts] = useState(0);

  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedScoringType, setSelectedScoringType] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  const [selectedParticipantType, setSelectedParticipantType] = useState("");

  const [pennantData, setPennantData] = useState({
    description: "",
    members: {},
    scoringType: {},
    pennantDay: "",
    startTime: "",
    endTime: "", // New endTime field
    playType: "",
    ageGroup: "",
    section: "",
    seasonName: "",
    startDate: null,
    endDate: null,
    courtsRequired: 0,
    participantType: "",
    sportType: "",
  });

  // Fetch settings (including totalCourts)
  useEffect(() => {
    const fetchPennantSettings = async () => {
      try {
        setLoading(true);
        const settingsDocRef = doc(db, "pennantSettings", "settingsDoc");
        const settingsSnapshot = await getDoc(settingsDocRef);

        // If no doc, fallback to empty object
        const settingsData = settingsSnapshot.exists()
          ? settingsSnapshot.data()
          : {};

        setSettings({
          seasons: settingsData.seasons || [],
          playTypes: settingsData.playTypes || [],
          ageGroups: settingsData.ageGroups || [],
          sections: settingsData.sections || [],
          pennantDays: settingsData.pennantDays || [],
          scoringTypes: settingsData.scoringTypes || [],
          participantTypes: settingsData.participantTypes || [],
          totalCourts: settingsData.totalCourts || 0, // read totalCourts
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching settings: ", error);
        setLoading(false);
      }
    };
    fetchPennantSettings();
  }, []);

  // Fetch *all* Pennants for overlap calculation
  useEffect(() => {
    const fetchAllPennants = async () => {
      try {
        const snap = await getDocs(collection(db, "Pennants"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllPennants(list);
      } catch (error) {
        console.error("Error fetching all pennants: ", error);
      }
    };
    fetchAllPennants();
  }, []);

  // If we are editing, fetch the existing Pennant data
  useEffect(() => {
    const fetchPennantData = async () => {
      if (edit && pennantId) {
        try {
          const pennantDocRef = doc(db, "Pennants", pennantId);
          const pennantSnapshot = await getDoc(pennantDocRef);
          const data = pennantSnapshot.data() || {};

          setPennantData({
            description: data.description || "",
            members: data.members || {},
            scoringType: data.scoringType || {},
            pennantDay: data.pennantDay || "",
            startTime: data.startTime || "",
            endTime: data.endTime || "", // populate endTime if present
            playType: data.playType || "",
            ageGroup: data.ageGroup || "",
            section: data.section || "",
            seasonName: data.seasonName || "",
            startDate: data.startDate || null,
            endDate: data.endDate || null,
            courtsRequired: data.courtsRequired || 0,
            participantType: data.participantType || "",
          });

          setSelectedSections(Object.keys(data.members || {}));
          setSelectedScoringType(data.scoringType?.id || "");
          setSelectedSeason(data.seasonName || "");
          setSelectedParticipantType(data.participantType || "");
        } catch (error) {
          console.error("Error fetching pennant data: ", error);
        }
      }
    };

    fetchPennantData();
  }, [edit, pennantId]);

  // Compute available courts based on overlapping Pennants on the same day/time
  useEffect(() => {
    if (
      !pennantData.pennantDay ||
      !pennantData.startTime ||
      !pennantData.endTime ||
      !settings.totalCourts
    ) {
      // If any critical piece is missing, default to totalCourts
      setAvailableCourts(settings.totalCourts || 0);
      return;
    }

    const ourStart = timeStringToMinutes(pennantData.startTime);
    const ourEnd = timeStringToMinutes(pennantData.endTime);

    if (ourEnd <= ourStart) {
      // If endTime is before or equal to startTime, no real range => show total
      setAvailableCourts(settings.totalCourts);
      return;
    }

    let totalUsed = 0;

    allPennants.forEach((p) => {
      // Skip if it's the same pennant in edit mode
      if (edit && p.id === pennantId) return;

      // Check only if same day and we have start/end times
      if (p.pennantDay === pennantData.pennantDay && p.startTime && p.endTime) {
        const pStart = timeStringToMinutes(p.startTime);
        const pEnd = timeStringToMinutes(p.endTime);
        if (pEnd > pStart) {
          // Overlap condition: ourStart < pEnd && pStart < ourEnd
          const isOverlap = ourStart < pEnd && pStart < ourEnd;
          if (isOverlap) {
            const used = parseInt(p.courtsRequired || "0", 10);
            totalUsed += used;
          }
        }
      }
    });

    const available = settings.totalCourts - totalUsed;
    setAvailableCourts(available < 0 ? 0 : available);
  }, [
    edit,
    pennantId,
    pennantData.pennantDay,
    pennantData.startTime,
    pennantData.endTime,
    settings.totalCourts,
    allPennants,
  ]);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    } else if (activeStep === steps.length - 1) {
      handleSubmit(); // Call handleSubmit only on the last step
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const handleClose = () => {
    toggle();
    clearState();
    setTimeout(() => setActiveStep(0), 500);
  };

  const clearState = () =>
    setPennantData({
      description: "",
      members: {},
      scoringType: {},
      pennantDay: "",
      startTime: "",
      endTime: "",
      playType: "",
      ageGroup: "",
      section: "",
      seasonName: "",
      startDate: null,
      endDate: null,
      courtsRequired: 0,
      participantType: "",
    });

  const handleSubmit = async () => {
    const scoringType = settings.scoringTypes.find(
      (type) => type.id === selectedScoringType
    );

    const updatedPennantData = {
      ...pennantData,
      name: generatePennantName(),
      scoringType,
      scoringTypeId: scoringType?.id || "",
      seasonName: selectedSeason,
      participantType: selectedParticipantType,
      members: selectedSections.reduce(
        (acc, section) => ({
          ...acc,
          [section]: pennantData.members[section] || [],
        }),
        {}
      ),
    };

    const pennantsCollection = collection(db, "Pennants");

    try {
      if (!edit) {
        await addDoc(pennantsCollection, updatedPennantData);
      } else {
        const pennantDocRef = doc(db, "Pennants", pennantId);
        await updateDoc(pennantDocRef, updatedPennantData);
      }

      setActiveStep(steps.length);
    } catch (error) {
      console.error("Error saving pennant: ", error);
    }
  };

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hoursNum = parseInt(hours, 10);
    const hours12 = hoursNum % 12 || 12;
    const ampm = hoursNum >= 12 ? "PM" : "AM";
    return `${hours12}:${minutes} ${ampm}`;
  };

  const generatePennantName = () => {
    const { sportType, startTime, endTime, pennantDay, ageGroup, playType } = pennantData;
    if (sportType && startTime && endTime && pennantDay && ageGroup && playType) {
      const formattedStart = formatTimeTo12Hour(startTime);
      const formattedEnd = formatTimeTo12Hour(endTime);
      return `${sportType} ${formattedStart} - ${formattedEnd} ${pennantDay} ${ageGroup} ${playType}`;
    }
    return "New Pennant";
  };
  

  const handleScoringTypeChange = (event) => {
    setSelectedScoringType(event.target.value);
    setPennantData({ ...pennantData, scoringTypeId: event.target.value });
  };

  const handleParticipantTypeChange = (event) => {
    setSelectedParticipantType(event.target.value);
    setPennantData({ ...pennantData, participantType: event.target.value });
  };

  const handleSeasonChange = (event) => {
    const selectedSeasonName = event.target.value;
    const selectedSeasonData = settings.seasons.find(
      (season) => season.name === selectedSeasonName
    );

    setSelectedSeason(selectedSeasonName);
    if (selectedSeasonData) {
      setPennantData({
        ...pennantData,
        seasonName: selectedSeasonName,
        startDate: selectedSeasonData.startDate,
        endDate: selectedSeasonData.endDate,
      });
    }
  };

  const handleSectionToggle = (section) => {
    setSelectedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const handleSelectAllSections = (selectAll) => {
    if (selectAll) {
      setSelectedSections(settings.sections.map((section) => section.name));
    } else {
      setSelectedSections([]);
    }
  };

  const handleChange = (event) => {
    setPennantData({ ...pennantData, [event.target.name]: event.target.value });
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {generatePennantName()}
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel id="sport-type-label">Sport Type</InputLabel>
              <Select
                labelId="sport-type-label"
                name="sportType"
                value={pennantData.sportType}
                onChange={handleChange}
                label="Sport Type"
              >
                <MenuItem value="Squash">Squash</MenuItem>
                <MenuItem value="Racquetball">Racquetball</MenuItem>
              </Select>
            </FormControl>


            {/* Season */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="season-label">Season</InputLabel>
              <Select
                labelId="season-label"
                name="seasonName"
                value={selectedSeason}
                onChange={handleSeasonChange}
                label="Season"
              >
                {settings.seasons.map((season) => (
                  <MenuItem key={season.id} value={season.name}>
                    {season.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Display season's start/end dates */}
            {pennantData.startDate && pennantData.endDate && (
              <Typography>
                Start Date:{" "}
                {new Date(pennantData.startDate).toLocaleDateString()}
                <br />
                End Date:{" "}
                {new Date(pennantData.endDate).toLocaleDateString()}
              </Typography>
            )}

            {/* Day */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="pennant-day-label">Pennant Day</InputLabel>
              <Select
                labelId="pennant-day-label"
                name="pennantDay"
                value={pennantData.pennantDay}
                onChange={handleChange}
                label="Pennant Day"
              >
                {settings.pennantDays.map((day) => (
                  <MenuItem key={day.id} value={day.name}>
                    {day.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Start Time */}
            <TextField
              label="Start Time"
              type="time"
              name="startTime"
              value={pennantData.startTime}
              onChange={handleChange}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />

            {/* End Time (New) */}
            <TextField
              label="End Time"
              type="time"
              name="endTime"
              value={pennantData.endTime}
              onChange={handleChange}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />

            {/* Participant Type */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="participant-type-label">
                Participant Type
              </InputLabel>
              <Select
                labelId="participant-type-label"
                value={selectedParticipantType}
                onChange={handleParticipantTypeChange}
                label="Participant Type"
              >
                {settings.participantTypes.map((type) => (
                  <MenuItem key={type.id} value={type.type}>
                    {type.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Play Type */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="play-type-label">Play Type</InputLabel>
              <Select
                labelId="play-type-label"
                name="playType"
                value={pennantData.playType}
                onChange={handleChange}
                label="Play Type"
              >
                {settings.playTypes.map((type) => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Age Group */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="age-group-label">Age Group</InputLabel>
              <Select
                labelId="age-group-label"
                name="ageGroup"
                value={pennantData.ageGroup}
                onChange={handleChange}
                label="Age Group"
              >
                {settings.ageGroups.map((group) => (
                  <MenuItem key={group.id} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Available Courts Display */}
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              Available Courts: {availableCourts}
            </Typography>

            {/* Courts Required */}
            <TextField
              label="Courts Required"
              type="number"
              name="courtsRequired"
              value={pennantData.courtsRequired}
              onChange={handleChange}
              fullWidth
              margin="normal"
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Box>
        );

      case 1:
        return loading ? (
          <CircularProgress />
        ) : (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="scoring-type-label">Scoring Type</InputLabel>
            <Select
              labelId="scoring-type-label"
              value={selectedScoringType}
              onChange={handleScoringTypeChange}
              label="Scoring Type"
            >
              {settings.scoringTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 2:
        return (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    selectedSections.length === settings.sections.length
                  }
                  onChange={(e) => handleSelectAllSections(e.target.checked)}
                />
              }
              label="Select All Sections"
            />
            {settings.sections.map((section) => (
              <FormControlLabel
                key={section.id}
                control={
                  <Checkbox
                    checked={selectedSections.includes(section.name)}
                    onChange={() => handleSectionToggle(section.name)}
                  />
                }
                label={section.name}
              />
            ))}
          </Box>
        );

      default:
        return (
          <Typography variant="h5" sx={{ mt: 3 }}>
            Pennant successfully {edit ? "updated" : "added"}!
          </Typography>
        );
    }
  }

  return (
    <Dialog open={isOpen}>
      <CloseButton onClose={handleClose} />
      <Container>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === steps.length ? (
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="h5">
                Pennant successfully {edit ? "updated" : "added"}!
              </Typography>
              <Button onClick={handleClose} variant="contained" sx={{ mt: 3 }}>
                Done
              </Button>
            </Box>
          ) : (
            <>
              {getStepContent(activeStep)}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
              >
                {activeStep > 0 && (
                  <Button onClick={handleBack} variant="contained">
                    Back
                  </Button>
                )}
                <Button onClick={handleNext} variant="contained">
                  {activeStep === steps.length - 1 ? "Finish" : "Next"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Dialog>
  );
}

AddPennant.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  edit: PropTypes.bool,
  pennantId: PropTypes.string,
};
